import { useState, useEffect } from 'react'
import { X, DownloadSimple, MagnifyingGlassPlus, MagnifyingGlassMinus, Spinner, Warning } from '@phosphor-icons/react'
import mammoth from 'mammoth'
import * as XLSX from 'xlsx'

interface DocumentViewerProps {
  isOpen: boolean
  onClose: () => void
  url: string
  fileName: string
  mimeType: string
}

type ViewerMode = 'pdf' | 'docx' | 'xlsx' | 'image' | 'unsupported'

function getViewerMode(mimeType: string): ViewerMode {
  if (mimeType === 'application/pdf') return 'pdf'
  // Spreadsheets — check before the generic "document" word match below.
  if (
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'application/vnd.ms-excel' ||
    mimeType.includes('spreadsheet') ||
    mimeType.includes('excel')
  ) return 'xlsx'
  if (mimeType.includes('word') || mimeType.includes('document') || mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'docx'
  if (mimeType.startsWith('image/')) return 'image'
  return 'unsupported'
}

// PDF Viewer Component - Using native browser viewer via iframe
function PdfViewer({ url }: { url: string }) {
  const [loading, setLoading] = useState(true)

  return (
    <div className="flex flex-col h-full">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <Spinner size={40} className="animate-spin text-emerald-500" />
          <span className="ml-3 text-gray-500">Carregando PDF...</span>
        </div>
      )}
      <iframe
        src={url}
        className="flex-1 w-full border-0"
        onLoad={() => setLoading(false)}
        title="PDF Viewer"
      />
    </div>
  )
}

// DOCX Viewer Component
function DocxViewer({ url }: { url: string }) {
  const [htmlContent, setHtmlContent] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)

    const loadDocx = async () => {
      try {
        // Fetch the DOCX file as array buffer
        const response = await fetch(url)
        const arrayBuffer = await response.arrayBuffer()

        // Convert to HTML using mammoth
        const result = await mammoth.convertToHtml({ arrayBuffer })
        setHtmlContent(result.value)

        if (result.messages.length > 0) {
          console.warn('Mammoth conversion messages:', result.messages)
        }
      } catch (err) {
        console.error('Error loading DOCX:', err)
        setError('Erro ao carregar documento Word')
      } finally {
        setLoading(false)
      }
    }

    loadDocx()
  }, [url])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size={40} className="animate-spin text-emerald-500" />
        <span className="ml-3 text-gray-500">Carregando documento...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-red-500">
        <Warning size={48} />
        <p className="mt-2">{error}</p>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-auto p-6 bg-white">
      <div
        className="max-w-4xl mx-auto prose prose-sm"
        dangerouslySetInnerHTML={{ __html: htmlContent }}
        style={{
          fontFamily: 'Arial, sans-serif',
          lineHeight: 1.6
        }}
      />
    </div>
  )
}

// XLSX / Spreadsheet Viewer Component — renders each sheet as an HTML table
function XlsxViewer({ url }: { url: string }) {
  const [sheets, setSheets] = useState<{ name: string; html: string }[]>([])
  const [active, setActive] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)

    const loadXlsx = async () => {
      try {
        const response = await fetch(url)
        const arrayBuffer = await response.arrayBuffer()
        const wb = XLSX.read(arrayBuffer, { type: 'array' })
        const parsed = wb.SheetNames.map((name) => ({
          name,
          html: XLSX.utils.sheet_to_html(wb.Sheets[name], { id: '', editable: false }),
        }))
        setSheets(parsed)
      } catch (err) {
        console.error('Error loading spreadsheet:', err)
        setError('Erro ao carregar planilha')
      } finally {
        setLoading(false)
      }
    }

    loadXlsx()
  }, [url])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size={40} className="animate-spin text-emerald-500" />
        <span className="ml-3 text-gray-500">Carregando planilha...</span>
      </div>
    )
  }

  if (error || sheets.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-red-500">
        <Warning size={48} />
        <p className="mt-2">{error || 'Planilha vazia'}</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {sheets.length > 1 && (
        <div className="flex gap-1 px-4 pt-3 border-b border-gray-200 bg-gray-50 overflow-x-auto">
          {sheets.map((s, i) => (
            <button
              key={s.name + i}
              onClick={() => setActive(i)}
              className={`px-3 py-1.5 text-sm rounded-t-lg whitespace-nowrap ${
                i === active
                  ? 'bg-white border border-b-0 border-gray-200 font-medium text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
      <div className="flex-1 overflow-auto p-4 bg-white xlsx-viewer">
        <div dangerouslySetInnerHTML={{ __html: sheets[active].html }} />
      </div>
      <style>{`
        .xlsx-viewer table { border-collapse: collapse; font-size: 13px; }
        .xlsx-viewer td, .xlsx-viewer th { border: 1px solid #e5e7eb; padding: 4px 8px; white-space: nowrap; }
        .xlsx-viewer tr:first-child td { background: #f9fafb; font-weight: 600; }
      `}</style>
    </div>
  )
}

// Image Viewer Component
function ImageViewer({ url, fileName }: { url: string; fileName: string }) {
  const [scale, setScale] = useState(1)

  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 3))
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.3))
  const resetZoom = () => setScale(1)

  return (
    <div className="flex flex-col h-full">
      {/* Controls */}
      <div className="flex items-center justify-center gap-4 p-3 bg-gray-100 border-b border-gray-200">
        <button
          onClick={zoomOut}
          className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <MagnifyingGlassMinus size={20} />
        </button>
        <button
          onClick={resetZoom}
          className="text-sm text-gray-600 px-3 py-1 rounded-lg hover:bg-gray-200 transition-colors"
        >
          {Math.round(scale * 100)}%
        </button>
        <button
          onClick={zoomIn}
          className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
        >
          <MagnifyingGlassPlus size={20} />
        </button>
      </div>

      {/* Image */}
      <div className="flex-1 overflow-auto bg-gray-200 p-4 flex items-center justify-center">
        <img
          src={url}
          alt={fileName}
          style={{ transform: `scale(${scale})`, transformOrigin: 'center' }}
          className="max-w-full max-h-full object-contain transition-transform shadow-lg"
        />
      </div>
    </div>
  )
}

// Unsupported Viewer Component
function UnsupportedViewer({ url, fileName, mimeType }: { url: string; fileName: string; mimeType: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-96 gap-4">
      <Warning size={64} className="text-gray-400" />
      <div className="text-center">
        <p className="text-lg font-medium text-gray-700">Visualizacao nao disponivel</p>
        <p className="text-sm text-gray-500 mt-1">
          Tipo de arquivo: {mimeType}
        </p>
      </div>
      <a
        href={url}
        download={fileName}
        className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
      >
        <DownloadSimple size={20} />
        Baixar arquivo
      </a>
    </div>
  )
}

// Main DocumentViewer Component
export function DocumentViewer({ isOpen, onClose, url, fileName, mimeType }: DocumentViewerProps) {
  const viewerMode = getViewerMode(mimeType)

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-[90vw] h-[90vh] max-w-6xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{fileName}</h3>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <a
              href={url}
              download={fileName}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Baixar"
            >
              <DownloadSimple size={24} />
            </a>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {viewerMode === 'pdf' && <PdfViewer url={url} />}
          {viewerMode === 'docx' && <DocxViewer url={url} />}
          {viewerMode === 'xlsx' && <XlsxViewer url={url} />}
          {viewerMode === 'image' && <ImageViewer url={url} fileName={fileName} />}
          {viewerMode === 'unsupported' && <UnsupportedViewer url={url} fileName={fileName} mimeType={mimeType} />}
        </div>
      </div>
    </div>
  )
}

export default DocumentViewer
